import { CmdKey } from "@milkdown/core";
import { useInstance } from "@milkdown/react";
import { callCommand } from "@milkdown/utils";
import {
  AtSign,
  Bold,
  Code,
  Code2,
  Heading1,
  Heading2,
  Heading3,
  Image,
  Italic,
  Link,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Quote,
  Strikethrough,
  Table,
} from "lucide-react";
import { useCallback } from "react";
import { Button } from "./button";
import { Separator } from "./separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

interface EditorToolbarProps {
  experimentId?: string;
  onMentionClick?: () => void;
}

interface ToolbarButton {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  tooltip: string;
  commandKey: string;
  payload?: unknown;
  disabled?: boolean;
}

type Call = <T>(command: CmdKey<T>, payload?: T) => void;

export function EditorToolbar({ experimentId, onMentionClick }: EditorToolbarProps) {
  const [isLoading, getInstance] = useInstance();
  const editor = getInstance();

  const call = useCallback<Call>(
    (command, payload) => {
      return editor?.action(callCommand(command, payload));
    },
    [editor],
  );

  const formattingButtons: ToolbarButton[] = [
    {
      id: "bold",
      icon: Bold,
      tooltip: "Bold (⌘+B)",
      commandKey: "ToggleStrong",
    },
    {
      id: "italic",
      icon: Italic,
      tooltip: "Italic (⌘+I)",
      commandKey: "ToggleEmphasis",
    },
    {
      id: "code",
      icon: Code,
      tooltip: "Inline Code (⌘+E)",
      commandKey: "ToggleInlineCode",
    },
    {
      id: "strikethrough",
      icon: Strikethrough,
      tooltip: "Strikethrough",
      commandKey: "ToggleStrikethrough",
    },
  ];

  const linkButton: ToolbarButton = {
    id: "link",
    icon: Link,
    tooltip: "Insert Link",
    commandKey: "ToggleLink",
  };

  const listButtons: ToolbarButton[] = [
    {
      id: "bullet-list",
      icon: List,
      tooltip: "Bullet List",
      commandKey: "WrapInBulletList",
    },
    {
      id: "ordered-list",
      icon: ListOrdered,
      tooltip: "Ordered List",
      commandKey: "WrapInOrderedList",
    },
    {
      id: "task-list",
      icon: ListTodo,
      tooltip: "Task List",
      commandKey: "TurnIntoTaskList",
    },
  ];

  const headingButtons: ToolbarButton[] = [
    {
      id: "heading-1",
      icon: Heading1,
      tooltip: "Heading 1",
      commandKey: "WrapInHeading",
      payload: 1,
    },
    {
      id: "heading-2",
      icon: Heading2,
      tooltip: "Heading 2",
      commandKey: "WrapInHeading",
      payload: 2,
    },
    {
      id: "heading-3",
      icon: Heading3,
      tooltip: "Heading 3",
      commandKey: "WrapInHeading",
      payload: 3,
    },
  ];

  const blockButtons: ToolbarButton[] = [
    {
      id: "blockquote",
      icon: Quote,
      tooltip: "Blockquote",
      commandKey: "WrapInBlockquote",
    },
    {
      id: "code-block",
      icon: Code2,
      tooltip: "Code Block",
      commandKey: "CreateCodeBlock",
    },
    {
      id: "divider",
      icon: Minus,
      tooltip: "Horizontal Rule",
      commandKey: "InsertHr",
    },
  ];

  const advancedButtons: ToolbarButton[] = [
    {
      id: "table",
      icon: Table,
      tooltip: "Insert Table",
      commandKey: "InsertTable",
    },
    {
      id: "image",
      icon: Image,
      tooltip: "Insert Image",
      commandKey: "InsertImage",
      disabled: !experimentId,
    },
  ];

  if (isLoading || !editor) {
    return null;
  }

  const handleButtonClick = (button: ToolbarButton) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      call(button.commandKey as unknown as CmdKey<unknown>, button.payload);
    } catch (error) {
      console.error(`Failed to execute command ${button.commandKey}:`, error);
    }
  };

  return (
    <TooltipProvider>
      <div className="editor-toolbar sticky top-0 z-10 flex flex-wrap items-center gap-1 overflow-hidden rounded-t-md border-b bg-background p-2">
        <div className="flex items-center gap-0.5">
          {formattingButtons.map(button => (
            <Tooltip key={button.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onMouseDown={handleButtonClick(button)}
                  aria-label={button.tooltip}
                  disabled={button.disabled}
                >
                  <button.icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{button.tooltip}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        <Separator orientation="vertical" className="h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onMouseDown={e => {
                e.preventDefault();
                onMentionClick?.();
              }}
              aria-label="Mention user"
            >
              <AtSign className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Mention user (@)</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onMouseDown={handleButtonClick(linkButton)}
              aria-label={linkButton.tooltip}
            >
              <linkButton.icon className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{linkButton.tooltip}</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6" />

        {/* Headings */}
        <div className="flex items-center gap-0.5">
          {headingButtons.map(button => (
            <Tooltip key={button.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onMouseDown={handleButtonClick(button)}
                  aria-label={button.tooltip}
                >
                  <button.icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{button.tooltip}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Lists */}
        <div className="flex items-center gap-0.5">
          {listButtons.map(button => (
            <Tooltip key={button.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onMouseDown={handleButtonClick(button)}
                  aria-label={button.tooltip}
                >
                  <button.icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{button.tooltip}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex items-center gap-0.5">
          {blockButtons.map(button => (
            <Tooltip key={button.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onMouseDown={handleButtonClick(button)}
                  aria-label={button.tooltip}
                >
                  <button.icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{button.tooltip}</TooltipContent>
            </Tooltip>
          ))}
        </div>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex items-center gap-0.5">
          {advancedButtons.map(button => (
            <Tooltip key={button.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onMouseDown={handleButtonClick(button)}
                  aria-label={button.tooltip}
                  disabled={button.disabled}
                >
                  <button.icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {button.disabled && button.id === "image" ? "Image upload requires experiment ID" : button.tooltip}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
